import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ShieldAlert, Unlock, RefreshCw, AlertCircle, Clock, Mail,
  CheckCircle2, XCircle, MessageSquare
} from 'lucide-react';
import { API } from '../../utils/apiConfig';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export const AdminFaceMatchTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState([]);
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // user_id or request_id mid-action
  const [decideModal, setDecideModal] = useState(null); // {req, decision}
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lockedRes, reqRes] = await Promise.all([
        axios.get(`${API}/admin/face-match/locked-users`, { headers: getHeaders() }),
        axios.get(`${API}/admin/face-match/requests?status=pending`, { headers: getHeaders() }),
      ]);
      setLocked(lockedRes.data.locked_users || []);
      setRequests(reqRes.data.requests || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // auto-refresh
    return () => clearInterval(id);
  }, [load]);

  const handleUnlock = async (userId, name) => {
    const confirmMsg = t('faceMatch.confirmUnlock',
      'User "{{name}}" wirklich entsperren? Alle fehlgeschlagenen Versuche werden gelöscht.',
      { name: name || userId });
    if (!window.confirm(confirmMsg)) return;
    setBusy(userId);
    try {
      await axios.post(
        `${API}/admin/face-match/${userId}/unlock`,
        { note: 'Direct admin override via dashboard', clear_attempts: true },
        { headers: getHeaders() }
      );
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleDecide = async () => {
    if (!decideModal) return;
    const { req, decision } = decideModal;
    setBusy(req.id);
    try {
      await axios.post(
        `${API}/admin/face-match/requests/${req.id}/decide`,
        { decision, admin_note: note.trim() || null },
        { headers: getHeaders() }
      );
      setDecideModal(null);
      setNote('');
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div style={s.card} data-testid="face-match-loading">
        <RefreshCw className="spin" size={18} />
        <span style={{ marginLeft: 8 }}>{t('common.loading', 'Lädt…')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...s.card, ...s.errorBox }} data-testid="face-match-error">
        <AlertCircle size={18} /> {error}
      </div>
    );
  }

  return (
    <div data-testid="face-match-tab">
      {/* ── Kacheln oben ────────────────────────────────────── */}
      <div style={s.statsGrid}>
        <div style={{ ...s.statCard, borderColor: '#ef4444' }}>
          <ShieldAlert size={20} style={{ color: '#fca5a5' }} />
          <div>
            <div style={s.statValue}>{locked.length}</div>
            <div style={s.statLabel}>{t('faceMatch.lockedUsers', 'Gesperrte User')}</div>
          </div>
        </div>
        <div style={{ ...s.statCard, borderColor: '#f59e0b' }}>
          <Mail size={20} style={{ color: '#fbbf24' }} />
          <div>
            <div style={s.statValue}>{requests.length}</div>
            <div style={s.statLabel}>{t('faceMatch.pendingRequests', 'Offene Anfragen')}</div>
          </div>
        </div>
        <div style={s.statCard}>
          <button style={s.refreshBtn} onClick={load} data-testid="face-match-refresh">
            <RefreshCw size={16} />
            {t('common.refresh', 'Aktualisieren')}
          </button>
        </div>
      </div>

      {/* ── Selfservice-Queue ──────────────────────────────── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <Mail size={18} />
          <h3 style={s.h3}>{t('faceMatch.supportQueue', 'Support-Queue (Selfservice-Anfragen)')}</h3>
          <span style={s.countBadge}>{requests.length}</span>
        </div>
        {requests.length === 0 ? (
          <div style={s.muted} data-testid="face-match-queue-empty">
            {t('faceMatch.queueEmpty', 'Keine offenen Anfragen. Alle Tickets sind sauber.')}
          </div>
        ) : (
          <div data-testid="face-match-queue-list">
            {requests.map((req) => (
              <div key={req.id} style={s.requestCard} data-testid={`face-match-req-${req.id}`}>
                <div style={s.requestHeader}>
                  <div>
                    <div style={s.userName}>{req.name || '—'}</div>
                    <div style={s.userEmail}>{req.email || '—'}</div>
                  </div>
                  <div style={s.requestMeta}>
                    <Clock size={12} />
                    <span>{req.created_at ? new Date(req.created_at).toLocaleString() : ''}</span>
                  </div>
                </div>
                <div style={s.requestReason}>
                  <MessageSquare size={14} style={{ flexShrink: 0, marginTop: 3 }} />
                  <span style={{ flex: 1 }}>{req.request_reason || '—'}</span>
                </div>
                <div style={s.requestActions}>
                  <button
                    data-testid={`face-match-approve-${req.id}`}
                    style={s.approveBtn}
                    disabled={busy === req.id}
                    onClick={() => setDecideModal({ req, decision: 'approve' })}
                  >
                    <CheckCircle2 size={14} />
                    {t('faceMatch.approve', 'Freischalten')}
                  </button>
                  <button
                    data-testid={`face-match-reject-${req.id}`}
                    style={s.rejectBtn}
                    disabled={busy === req.id}
                    onClick={() => setDecideModal({ req, decision: 'reject' })}
                  >
                    <XCircle size={14} />
                    {t('faceMatch.reject', 'Ablehnen')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Locked-Users Tabelle ───────────────────────────── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <ShieldAlert size={18} />
          <h3 style={s.h3}>{t('faceMatch.lockedTitle', 'Gesperrte User (failed ≥ 3)')}</h3>
          <span style={s.countBadge}>{locked.length}</span>
        </div>
        {locked.length === 0 ? (
          <div style={s.muted} data-testid="face-match-locked-empty">
            {t('faceMatch.lockedEmpty', 'Niemand gesperrt. ✓')}
          </div>
        ) : (
          <table style={s.table} data-testid="face-match-locked-table">
            <thead>
              <tr>
                <th style={s.th}>{t('faceMatch.colUser', 'User')}</th>
                <th style={s.th}>{t('faceMatch.colFails', 'Fehler')}</th>
                <th style={s.th}>{t('faceMatch.colLockedUntil', 'Gesperrt bis')}</th>
                <th style={s.th}>{t('faceMatch.colLastFail', 'Letzter Fehlschlag')}</th>
                <th style={s.th}>{t('faceMatch.colPending', 'Pending Req.')}</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {locked.map((u) => (
                <tr key={u.user_id} data-testid={`face-match-locked-row-${u.user_id}`}>
                  <td style={s.td}>
                    <div style={{ fontWeight: 600 }}>{u.name || '—'}</div>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>{u.email || '—'}</div>
                  </td>
                  <td style={s.td}>
                    <span style={s.failsBadge}>{u.face_match_failed_count || 0}</span>
                  </td>
                  <td style={s.td}>
                    {u.face_match_locked_until
                      ? new Date(u.face_match_locked_until).toLocaleString()
                      : '—'}
                  </td>
                  <td style={s.td}>
                    {u.last_failed_at
                      ? new Date(u.last_failed_at).toLocaleString()
                      : '—'}
                  </td>
                  <td style={s.td}>
                    {u.pending_requests > 0 ? (
                      <span style={s.pendingPill}>{u.pending_requests}</span>
                    ) : (
                      <span style={s.muted}>0</span>
                    )}
                  </td>
                  <td style={s.td}>
                    <button
                      data-testid={`face-match-unlock-${u.user_id}`}
                      style={s.unlockBtn}
                      disabled={busy === u.user_id}
                      onClick={() => handleUnlock(u.user_id, u.name)}
                    >
                      <Unlock size={14} />
                      {busy === u.user_id
                        ? t('common.loading', '…')
                        : t('faceMatch.unlock', '1-Click Unlock')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Decide Modal ───────────────────────────────────── */}
      {decideModal && (
        <div style={s.backdrop} onClick={() => { setDecideModal(null); setNote(''); }}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.h3}>
              {decideModal.decision === 'approve'
                ? t('faceMatch.modalApproveTitle', 'Anfrage genehmigen')
                : t('faceMatch.modalRejectTitle', 'Anfrage ablehnen')}
            </h3>
            <div style={{ ...s.muted, marginTop: 8 }}>
              <strong>{decideModal.req.name}</strong> — {decideModal.req.email}
            </div>
            <div style={{ ...s.requestReason, marginTop: 12 }}>
              <MessageSquare size={14} style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{decideModal.req.request_reason}</span>
            </div>
            <label style={s.label}>{t('faceMatch.adminNote', 'Notiz (optional)')}</label>
            <textarea
              data-testid="face-match-decide-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={s.textarea}
              placeholder={t('faceMatch.notePlaceholder',
                'Z.B. „Per Telefon verifiziert" oder „Mehrere Profile, Verdacht auf Betrug"')}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                data-testid="face-match-decide-cancel"
                style={s.secondaryBtn}
                onClick={() => { setDecideModal(null); setNote(''); }}
              >
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button
                data-testid="face-match-decide-confirm"
                style={decideModal.decision === 'approve' ? s.approveBtnLarge : s.rejectBtnLarge}
                disabled={busy === decideModal.req.id}
                onClick={handleDecide}
              >
                {busy === decideModal.req.id
                  ? t('common.loading', 'Lädt…')
                  : decideModal.decision === 'approve'
                    ? t('faceMatch.confirmApprove', 'Freischalten')
                    : t('faceMatch.confirmReject', 'Ablehnen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  card: {
    background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(22px)',
    border: '1px solid rgba(148,163,184,0.18)', borderRadius: 18,
    padding: 22, marginBottom: 16, color: '#e2e8f0',
  },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 18,
  },
  statCard: {
    background: 'rgba(15,23,42,0.6)',
    border: '1px solid rgba(148,163,184,0.18)', borderRadius: 16,
    padding: 16, display: 'flex', alignItems: 'center', gap: 14, color: '#e2e8f0',
  },
  statValue: { fontSize: 28, fontWeight: 700, lineHeight: 1 },
  statLabel: { fontSize: 11, opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 4 },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
    border: '1px solid rgba(99,102,241,0.3)', padding: '10px 18px',
    borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  h3: { fontSize: 16, margin: 0, fontWeight: 600 },
  muted: { fontSize: 13, opacity: 0.65 },
  countBadge: {
    marginLeft: 'auto', background: 'rgba(99,102,241,0.18)', color: '#a5b4fc',
    fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10,
  },
  requestCard: {
    background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(245,158,11,0.25)',
    borderRadius: 14, padding: 16, marginBottom: 12,
  },
  requestHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  userName: { fontWeight: 700, fontSize: 14 },
  userEmail: { fontSize: 12, opacity: 0.65, marginTop: 2 },
  requestMeta: { fontSize: 11, opacity: 0.55, display: 'flex', alignItems: 'center', gap: 4 },
  requestReason: {
    margin: '12px 0', padding: '10px 12px',
    background: 'rgba(255,255,255,0.04)', borderRadius: 8,
    fontSize: 13, lineHeight: 1.5, display: 'flex', gap: 8,
  },
  requestActions: { display: 'flex', gap: 8, marginTop: 8 },
  approveBtn: {
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac',
    border: '1px solid rgba(34,197,94,0.35)', padding: '8px 14px',
    borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  rejectBtn: {
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, background: 'rgba(239,68,68,0.12)', color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.3)', padding: '8px 14px',
    borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', fontSize: 11, opacity: 0.65, textTransform: 'uppercase',
    letterSpacing: 0.5, padding: '8px 6px', borderBottom: '1px solid rgba(148,163,184,0.15)',
  },
  td: { padding: '10px 6px', fontSize: 13, borderBottom: '1px solid rgba(148,163,184,0.08)' },
  failsBadge: {
    background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
    padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 700,
  },
  pendingPill: {
    background: 'rgba(245,158,11,0.15)', color: '#fbbf24',
    padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
  },
  unlockBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'linear-gradient(135deg, #ef4444, #f97316)',
    color: '#fff', border: 'none', padding: '8px 14px',
    borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(239,68,68,0.3)',
  },
  errorBox: {
    background: 'rgba(239,68,68,0.1)', color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10,
    padding: 14, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10,
  },
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  },
  modal: {
    background: '#0f172a', border: '1px solid rgba(148,163,184,0.22)',
    borderRadius: 18, padding: 26, width: 'min(500px, 92vw)', color: '#e2e8f0',
  },
  label: { display: 'block', fontSize: 12, opacity: 0.7, marginTop: 16, marginBottom: 6 },
  textarea: {
    width: '100%', padding: '10px 12px', background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(148,163,184,0.22)', borderRadius: 10,
    color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box', resize: 'vertical',
    fontFamily: 'inherit',
  },
  secondaryBtn: {
    flex: 1, background: 'rgba(255,255,255,0.06)', color: '#e2e8f0',
    border: '1px solid rgba(148,163,184,0.22)', padding: '10px 16px',
    borderRadius: 10, cursor: 'pointer', fontSize: 13,
  },
  approveBtnLarge: {
    flex: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: '#fff', border: 'none', padding: '10px 16px',
    borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13,
  },
  rejectBtnLarge: {
    flex: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
    color: '#fff', border: 'none', padding: '10px 16px',
    borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13,
  },
};
